import type { ShopTheLookItem } from "@reco/shared/src/types/recommendation";
import { computed, onBeforeUnmount, onMounted, reactive, type Ref } from "vue";

export const MIN_STL_ITEMS = 4;
const STL_LOAD_TIMEOUT_MS = 10000;
const STL_ITEMS_ADDED_EVENT = "stl-slider:items-added";

export enum FragmentState {
  LOADING = "loading",
  LOADED = "loaded",
  FAILED = "failed",
}

/**
 * Composable that tracks which STL fragments were successfully loaded by CMS's STL script.
 * @param shopTheLookItems The list of STLs to be rendered, as received from the Reco API
 * @param containerRef Ref to the container element in which the STL fragments will be rendered (used to query the DOM for the fragments)
 * @returns The shoTheLookItems that should be rendered
 */
export function useLoadSTLFragments(
  shopTheLookItems: ShopTheLookItem[] | undefined,
  containerRef: Ref<HTMLElement | null>,
) {
  const fragmentStates = reactive<Record<string, FragmentState>>(
    shopTheLookItems?.reduce(
      (acc, item) => {
        acc[item.itemId] = FragmentState.LOADING;
        return acc;
      },
      {} as Record<string, FragmentState>,
    ) ?? {},
  );

  const originalElements = new Map<string, HTMLElement>();
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const resolveStates = () => {
    for (const [stlId, originalEl] of originalElements) {
      if (fragmentStates[stlId] !== FragmentState.LOADING) continue;

      fragmentStates[stlId] = originalEl.isConnected ? FragmentState.FAILED : FragmentState.LOADED;
    }
  };

  const onItemsAdded = () => {
    if (timeoutId) clearTimeout(timeoutId);
    document.removeEventListener(STL_ITEMS_ADDED_EVENT, onItemsAdded);
    resolveStates();
  };

  onMounted(() => {
    const wrappers = containerRef.value?.querySelectorAll<HTMLElement>("[data-stl-id]");
    wrappers?.forEach((wrapper) => {
      if (wrapper.dataset.stlId) {
        originalElements.set(wrapper.dataset.stlId, wrapper);
      }
    });

    // The CMS script listens for the DOMContentLoaded event to load the STL Fragments
    // Since our Widgets are mounted after that event fires, we need to manually dispatch it here again
    document.dispatchEvent(new Event("DOMContentLoaded"));
    document.addEventListener(STL_ITEMS_ADDED_EVENT, onItemsAdded);

    timeoutId = setTimeout(() => {
      document.removeEventListener(STL_ITEMS_ADDED_EVENT, onItemsAdded);
      resolveStates();
    }, STL_LOAD_TIMEOUT_MS);
  });

  onBeforeUnmount(() => {
    if (timeoutId) clearTimeout(timeoutId);
    document.removeEventListener(STL_ITEMS_ADDED_EVENT, onItemsAdded);
  });

  const visibleItems = computed(() => {
    return (shopTheLookItems ?? []).filter((item) => fragmentStates[item.itemId] !== FragmentState.FAILED);
  });

  return { visibleItems };
}
